import { useState, useRef, useEffect, useCallback } from 'react'
import type { QuestionProps } from '../eform.types'

// ── Google Places Address Autocomplete (new API) ────────────────────────────

interface PlacesSuggestion {
  placeId: string
  mainText: string
  secondaryText: string
  prediction: any
}

function GooglePlacesStreetInput({
  value,
  onChange,
  onSelect,
  className,
  placeholder,
  country,
}: {
  value: string
  onChange: (street: string) => void
  onSelect: (address: { street: string; city: string; state: string; zip: string; country: string }) => void
  className: string
  placeholder: string
  country: string
}) {
  const [suggestions, setSuggestions] = useState<PlacesSuggestion[]>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [googleReady, setGoogleReady] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const sessionTokenRef = useRef<any>(null)

  useEffect(() => {
    let cancelled = false
    async function init() {
      while (!(window as any).google?.maps?.importLibrary) {
        if (cancelled) return
        await new Promise((r) => setTimeout(r, 500))
      }
      await (window as any).google.maps.importLibrary('places')
      if (!cancelled) setGoogleReady(true)
    }
    init()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  // Reached through `window`, the way the guard on the next line already does.
  // The Places SDK is loaded externally, so there is no module to import and
  // no ambient `google` for tsc to find — the two bare references here were
  // TS2304. Adding @types/google.maps for it would be a dependency for an SDK
  // this app otherwise moved off: address autocomplete elsewhere is keyless
  // Nominatim.
  function places(): any {
    return (window as any).google?.maps?.places
  }

  function getSessionToken() {
    const p = places()
    if (!sessionTokenRef.current && p?.AutocompleteSessionToken) {
      sessionTokenRef.current = new p.AutocompleteSessionToken()
    }
    return sessionTokenRef.current
  }

  function search(q: string) {
    if (timerRef.current) clearTimeout(timerRef.current)
    if (q.trim().length < 3 || !googleReady) {
      setSuggestions([])
      setOpen(false)
      return
    }
    timerRef.current = setTimeout(async () => {
      setLoading(true)
      try {
        const request: any = {
          input: q,
          includedPrimaryTypes: ['street_address', 'subpremise', 'premise'],
          includedRegionCodes: [country.toLowerCase()],
          sessionToken: getSessionToken(),
        }
        const { suggestions: results } =
          await places().AutocompleteSuggestion.fetchAutocompleteSuggestions(request)

        const mapped: PlacesSuggestion[] = results
          .filter((s: any) => s.placePrediction)
          .map((s: any) => ({
            placeId: s.placePrediction.placeId,
            mainText: s.placePrediction.mainText?.text || s.placePrediction.text?.text || '',
            secondaryText: s.placePrediction.secondaryText?.text || '',
            prediction: s.placePrediction,
          }))
        setSuggestions(mapped)
        setOpen(mapped.length > 0)
      } catch (err) {
        console.error('[AddressField] Places error:', err)
        setSuggestions([])
      } finally {
        setLoading(false)
      }
    }, 300)
  }

  async function pick(suggestion: PlacesSuggestion) {
    setOpen(false)
    setSuggestions([])
    onChange(suggestion.mainText)

    try {
      const place = suggestion.prediction.toPlace()
      await place.fetchFields({ fields: ['addressComponents'] })
      sessionTokenRef.current = null

      let street = ''
      let city = ''
      let state = ''
      let zip = ''
      let countryCode = ''

      for (const comp of place.addressComponents || []) {
        const types: string[] = comp.types || []
        if (types.includes('street_number')) {
          street = comp.longText + ' ' + street
        } else if (types.includes('route')) {
          street = street + comp.longText
        } else if (types.includes('locality') || types.includes('sublocality_level_1')) {
          city = comp.longText
        } else if (types.includes('administrative_area_level_1')) {
          state = comp.shortText
        } else if (types.includes('postal_code')) {
          zip = comp.longText
        } else if (types.includes('country')) {
          countryCode = comp.shortText
        }
      }

      street = street.trim()
      onSelect({ street, city, state, zip, country: countryCode })
    } catch (err) {
      console.error('[AddressField] Place details error:', err)
    }
  }

  return (
    <div ref={wrapperRef} className="relative">
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        className={className}
        onChange={(e) => {
          onChange(e.target.value)
          search(e.target.value)
        }}
        onFocus={() => {
          if (suggestions.length) setOpen(true)
        }}
      />
      {open && (loading || suggestions.length > 0) && (
        <ul className="absolute left-0 right-0 z-[100000] mt-1 max-h-64 overflow-auto rounded-lg border border-slate-200 bg-white text-sm shadow-lg">
          {loading && suggestions.length === 0 && (
            <li className="px-3 py-2 text-slate-400">Searching...</li>
          )}
          {suggestions.map((s) => (
            <li
              key={s.placeId}
              className="cursor-pointer px-3 py-2 hover:bg-slate-50"
              onMouseDown={(e) => {
                e.preventDefault()
                pick(s)
              }}
            >
              <div className="font-medium text-slate-800">{s.mainText}</div>
              <div className="truncate text-xs text-slate-500">{s.secondaryText}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ── State/Province Autocomplete & Address Field ─────────────────────────────

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC',
]

const US_STATE_NAMES: Record<string, string> = {
  AL:'Alabama',AK:'Alaska',AZ:'Arizona',AR:'Arkansas',CA:'California',CO:'Colorado',
  CT:'Connecticut',DE:'Delaware',FL:'Florida',GA:'Georgia',HI:'Hawaii',ID:'Idaho',
  IL:'Illinois',IN:'Indiana',IA:'Iowa',KS:'Kansas',KY:'Kentucky',LA:'Louisiana',
  ME:'Maine',MD:'Maryland',MA:'Massachusetts',MI:'Michigan',MN:'Minnesota',MS:'Mississippi',
  MO:'Missouri',MT:'Montana',NE:'Nebraska',NV:'Nevada',NH:'New Hampshire',NJ:'New Jersey',
  NM:'New Mexico',NY:'New York',NC:'North Carolina',ND:'North Dakota',OH:'Ohio',OK:'Oklahoma',
  OR:'Oregon',PA:'Pennsylvania',RI:'Rhode Island',SC:'South Carolina',SD:'South Dakota',
  TN:'Tennessee',TX:'Texas',UT:'Utah',VT:'Vermont',VA:'Virginia',WA:'Washington',
  WV:'West Virginia',WI:'Wisconsin',WY:'Wyoming',DC:'District of Columbia',
}

const CA_PROVINCES = [
  'AB','BC','MB','NB','NL','NS','NT','NU','ON','PE','QC','SK','YT',
]

const CA_PROVINCE_NAMES: Record<string, string> = {
  AB:'Alberta',BC:'British Columbia',MB:'Manitoba',NB:'New Brunswick',
  NL:'Newfoundland and Labrador',NS:'Nova Scotia',NT:'Northwest Territories',
  NU:'Nunavut',ON:'Ontario',PE:'Prince Edward Island',QC:'Quebec',
  SK:'Saskatchewan',YT:'Yukon',
}

const COUNTRY_OPTIONS = [
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
]

function StateAutocomplete({
  value,
  onChange,
  className,
  country = 'US',
}: {
  value: string
  onChange: (val: string) => void
  className: string
  country?: string
}) {
  const [open, setOpen] = useState(false)
  const [highlightIdx, setHighlightIdx] = useState(-1)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  const states = country === 'CA' ? CA_PROVINCES : US_STATES
  const names = country === 'CA' ? CA_PROVINCE_NAMES : US_STATE_NAMES

  const query = value.trim().toLowerCase()
  const filtered = query
    ? states.filter(
        (s) => s.toLowerCase().startsWith(query) || (names[s]?.toLowerCase().startsWith(query)),
      )
    : states

  const showDropdown = open && filtered.length > 0

  const selectState = useCallback(
    (s: string) => {
      onChange(s)
      setOpen(false)
      setHighlightIdx(-1)
    },
    [onChange],
  )

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (highlightIdx >= 0 && listRef.current) {
      const el = listRef.current.children[highlightIdx] as HTMLElement | undefined
      el?.scrollIntoView({ block: 'nearest' })
    }
  }, [highlightIdx])

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!showDropdown) {
      if (e.key === 'ArrowDown') {
        setOpen(true)
        setHighlightIdx(0)
        e.preventDefault()
      }
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightIdx((i) => (i < filtered.length - 1 ? i + 1 : 0))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightIdx((i) => (i > 0 ? i - 1 : filtered.length - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (highlightIdx >= 0 && highlightIdx < filtered.length) {
        selectState(filtered[highlightIdx])
      }
    } else if (e.key === 'Escape') {
      setOpen(false)
      setHighlightIdx(-1)
    }
  }

  return (
    <div ref={wrapperRef} className="relative">
      <input
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
          setOpen(true)
          setHighlightIdx(-1)
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={country === 'CA' ? 'Province / Territory' : 'State / Province'}
        className={className}
        autoComplete="off"
      />
      {showDropdown && (
        <ul
          ref={listRef}
          className="absolute z-20 mt-1 max-h-48 w-full overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg"
        >
          {filtered.map((s, i) => (
            <li
              key={s}
              onMouseDown={(e) => {
                e.preventDefault()
                selectState(s)
              }}
              className={`cursor-pointer px-3 py-2 text-sm ${
                i === highlightIdx ? 'bg-primary/10 text-primary' : 'text-slate-700 hover:bg-slate-50'
              }`}
            >
              <span className="font-medium">{s}</span>
              <span className="ml-1.5 text-slate-400">{names[s]}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default function AddressField({ question, value, onChange, error, accountCountry }: QuestionProps) {
  const defaultCountry = accountCountry || 'US'
  const strValue = Array.isArray(value) ? value[0] ?? '' : value
  const parts = strValue.split('|')
  const addr1 = parts[0] || ''
  const addr2 = parts[1] || ''
  const city = parts[2] || ''
  const state = parts[3] || ''
  const zip = parts[4] || ''
  const country = parts[5] || defaultCountry

  const req = question.isRequired
  const mask = req ? (question.customTypeId || 15) : 0
  const reqAddr = (mask & 1) !== 0
  const reqCity = (mask & 2) !== 0
  const reqState = (mask & 4) !== 0
  const reqZip = (mask & 8) !== 0

  function update(field: number, val: string) {
    const newParts = [addr1, addr2, city, state, zip, country]
    newParts[field] = val
    onChange(newParts.join('|'))
  }

  function handleCountryChange(newCountry: string) {
    const newParts = [addr1, addr2, city, '', zip, newCountry]
    onChange(newParts.join('|'))
  }

  const baseClass = 'w-full rounded-lg border px-4 py-3 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary'
  const errBorder = 'border-red-300'
  const normalBorder = 'border-slate-200'

  const missingAddr = reqAddr && error && !addr1.trim()
  const missingCity = reqCity && error && !city.trim()
  const missingState = reqState && error && !state.trim()
  const missingZip = reqZip && error && !zip.trim()

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-slate-500 mb-1">
          Street Address {reqAddr && <span className="text-red-500">*</span>}
        </label>
        <GooglePlacesStreetInput
          value={addr1}
          onChange={(street) => update(0, street)}
          onSelect={(parsed) => {
            const newParts = [parsed.street, addr2, parsed.city, parsed.state, parsed.zip, parsed.country || country]
            onChange(newParts.join('|'))
          }}
          className={`${baseClass} ${missingAddr ? errBorder : normalBorder}`}
          placeholder="Start typing an address..."
          country={country}
        />
        {missingAddr && <p className="text-xs text-red-500 mt-1">Street address is required</p>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Address Line 2</label>
          <input
            type="text"
            value={addr2}
            onChange={(e) => update(1, e.target.value)}
            placeholder="Apt, suite, etc."
            className={`${baseClass} ${normalBorder}`}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Country</label>
          <select
            value={country}
            onChange={(e) => handleCountryChange(e.target.value)}
            className={`${baseClass} ${normalBorder}`}
          >
            {COUNTRY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">
            City {reqCity && <span className="text-red-500">*</span>}
          </label>
          <input
            type="text"
            value={city}
            onChange={(e) => update(2, e.target.value)}
            placeholder="City"
            className={`${baseClass} ${missingCity ? errBorder : normalBorder}`}
          />
          {missingCity && <p className="text-xs text-red-500 mt-1">City is required</p>}
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">
            {country === 'CA' ? 'Province' : 'State'} {reqState && <span className="text-red-500">*</span>}
          </label>
          <StateAutocomplete
            value={state}
            onChange={(val) => update(3, val)}
            className={`${baseClass} ${missingState ? errBorder : normalBorder}`}
            country={country}
          />
          {missingState && <p className="text-xs text-red-500 mt-1">{country === 'CA' ? 'Province' : 'State'} is required</p>}
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">
            {country === 'CA' ? 'Postal Code' : 'Zip Code'} {reqZip && <span className="text-red-500">*</span>}
          </label>
          <input
            type="text"
            value={zip}
            onChange={(e) => update(4, e.target.value)}
            placeholder={country === 'CA' ? 'Postal code' : 'Zip'}
            className={`${baseClass} ${missingZip ? errBorder : normalBorder}`}
            maxLength={country === 'CA' ? 7 : 9}
          />
          {missingZip && <p className="text-xs text-red-500 mt-1">{country === 'CA' ? 'Postal code' : 'Zip code'} is required</p>}
        </div>
      </div>
    </div>
  )
}
